/**
 * An agent's FULL-SCREEN surface — the whole sub-app, once.
 *
 * The other half of AgentFrame. An agent declares its face and its route tree
 * (agentRoutes.tsx); this mounts them at the agent's own subdomain, that
 * mounts them in Home's pane, and an agent supplies neither surface's
 * plumbing.
 *
 * It exists because ten apps/<slug>/src/App.tsx files were the same ~100-line
 * program — providers, auth gate, login bounce, i18n, routes — and hand-copied
 * programs drift. They had, silently:
 *
 *   - FIVE of ten missing the "follow the signed-in user's language" effect,
 *     so a nurse whose profile says `de` read English on care-planner, compatibility,
 *     knowledge-profile, roster and alg while the app beside them was German.
 *   - SIX of ten never mounting SessionEventSubscriber, whose own doc says it
 *     "MUST be mounted by every app that hosts a chat surface" — without it a
 *     sent message shows a thinking placeholder that never resolves, because
 *     the answer arrives on the /events channel nobody subscribed to. Four of
 *     those six argued in a comment that usePendingTurnPoll covers them, which
 *     is true as a catch-up and is not the same delivery; one per-app judgement
 *     call about a platform contract is one too many, so it is unconditional.
 *   - Every one of them retrying 403s (see lib/queryClient).
 *
 * The language sync and the retry policy were not decisions at all — they were
 * nine chances to forget. Which is why the fix is one implementation rather
 * than nine corrections.
 */
import { useEffect, type ComponentType, type ReactElement } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { isDevPreviewSurface } from '@/api/client'
import { mainAppLoginUrl } from '@/api/mainAppUrl'
import { Spinner } from '@/components/ui/spinner'
import { Toaster } from '@/components/ui/toaster'
import { SessionEventSubscriber } from '@/components/session'
import { useAuthInit } from '@/hooks/useAuthInit'
import { useConfigLoader } from '@/hooks/useConfigLoader'
import { createAppQueryClient } from '@/lib/queryClient'
import { useAuthStore } from '@/stores/auth'
// `import type` is load-bearing: a VALUE import of agentRoutes here would pull
// every agent's route tree — and every screen behind it — into all ten app
// bundles. The type is erased before the bundler sees a dependency.
import type { AgentRoute } from './agentRoutes'
import { AgentScreenFallback } from './AgentScreenFallback'

const queryClient = createAppQueryClient()

export interface AgentAppProps {
  /**
   * The screen at `/`. In the frame that is the agent's face alone, because
   * Home's pane supplies the chrome around it; full-screen the app supplies
   * its own (title bar, MainAppNav, the return-to-twin control), so this is
   * usually the app's root page composing the same face.
   */
  home: ReactElement
  /** The agent's declared depth (apps/<slug>/src/routes.tsx), mounted identically here. */
  routes?: AgentRoute[]
  /**
   * The app's own `/sessions/:id` screen: a conversation with this agent,
   * full-screen. Not part of `routes` — in the frame that is Home's pane, so
   * it is the one screen the two surfaces genuinely do not share.
   */
  sessionScreen?: ReactElement
  /** DEV-only `/preview` harness (lazy, dead-code-eliminated in prod builds). */
  devPreview?: ComponentType
}

function AgentAppRoutes({ home, routes, sessionScreen, devPreview: Preview }: AgentAppProps) {
  const { t } = useTranslation()
  const { isInitializing, isAuthenticated } = useAuthInit()
  // Populate the config store so branding, i18n and the agent's identity are
  // available app-wide. /config is public + idempotent — fine to fire
  // alongside auth.
  useConfigLoader()

  const loading = (
    <div className="flex h-dvh items-center justify-center bg-[var(--color-surface-canvas,#ffffff)]">
      <Spinner className="h-6 w-6 text-[var(--color-surface-mute)]" />
      <span className="sr-only">{t('common.loading', 'Loading...')}</span>
    </div>
  )

  if (isInitializing) return loading

  // DEV-only design preview (no auth, no backend). The shared API client knows
  // the same surface and skips its 401 → /login redirect there.
  if (Preview && isDevPreviewSurface()) {
    return (
      <AgentScreenFallback>
        <Preview />
      </AgentScreenFallback>
    )
  }

  if (!isAuthenticated) {
    // Log in at the main app, then come back here (returnTo). The root-host
    // derivation + dev-port mapping live in the shared mainAppUrl helper.
    window.location.href = mainAppLoginUrl()
    return null
  }

  return (
    <>
      {/* Async-turn answer delivery. Unconditional: the hook self-guards on
          auth and no-ops without a token, so the only thing per-app judgement
          bought here was six apps whose chat silently never answered. */}
      <SessionEventSubscriber />
      {/* One boundary for every lazily-loaded screen an agent declares (alg's
          Studio pulls in React Flow + Yjs, learn's runner its quiz views) — so
          a heavy chunk never needs a per-app decision. */}
      <AgentScreenFallback>
        <Routes>
          <Route path="/" element={home} />
          {sessionScreen && <Route path="/sessions/:id" element={sessionScreen} />}
          {(routes ?? []).map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AgentScreenFallback>
    </>
  )
}

export function AgentApp(props: AgentAppProps) {
  const { i18n } = useTranslation()

  // Follow the signed-in user's language. Without it the app stays on the
  // i18n-detected BROWSER language, so a nurse whose profile says `de` reads
  // English in one agent and German in the next.
  const userLanguage = useAuthStore((state) => state.user?.language)
  useEffect(() => {
    if (userLanguage && userLanguage !== i18n.language) {
      void i18n.changeLanguage(userLanguage)
    }
  }, [userLanguage, i18n])

  // Sync the document lang attribute with the i18n locale for screen readers.
  useEffect(() => {
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AgentAppRoutes {...props} />
        <Toaster />
      </QueryClientProvider>
    </BrowserRouter>
  )
}
